import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import { submitLead } from "@/lib/leads.functions";
import { toast } from "sonner";
import { getStoredUtm } from "@/lib/tracking";
import { SHARE_IMAGES } from "@/lib/share-images";
import { BUSINESS, ADDRESS_LINES } from "@/lib/business-info";

export const Route = createFileRoute("/contact")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("contact")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Contact — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "Get in touch with Glow Long Golf — questions about orders, bulk pricing, partnerships, or press." },
      { property: "og:title", content: loaderData?.title || "Contact — Glow Long Golf" },
      { property: "og:description", content: loaderData?.meta_description || "Get in touch with Glow Long Golf — questions about orders, bulk pricing, partnerships, or press." },
      { property: "og:url", content: "https://glowlonggolf.com/contact" },
      { property: "og:image", content: SHARE_IMAGES.macroDew },
      { name: "twitter:image", content: SHARE_IMAGES.macroDew },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/contact" }],
  }),

  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: ContactPage,
});

function ContactPage() {
  const [loading, setLoading] = useState(false);
  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    const fd = new FormData(e.currentTarget);
    const res = await submitLead({
      data: {
        type: "contact",
        email: fd.get("email") as string,
        name: fd.get("name") as string,
        source: "contact_page",
        utm: getStoredUtm(),
        payload: { subject: fd.get("subject"), message: fd.get("message") },
      },
    });
    setLoading(false);
    if ((res as any).ok) { toast.success("Sent — we'll reply soon"); (e.target as HTMLFormElement).reset(); }
    else toast.error("Could not send");
  }
  return (
    <EditablePageShell slug="contact" defaults={{ eyebrow: "Contact", title: "Get in touch", lead: "Questions about orders, events, or anything else? Drop us a note." }}>
      <section className="mx-auto max-w-2xl px-4 pb-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 rounded-3xl border border-border bg-card p-6 sm:grid-cols-3">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">Call us</h2>
            <a href={`tel:${BUSINESS.phoneHref}`} className="mt-2 block text-sm text-muted-foreground transition hover:text-foreground">
              {BUSINESS.phone}
            </a>
          </div>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">Email</h2>
            <a href={`mailto:${BUSINESS.email}`} className="mt-2 block text-sm text-muted-foreground transition hover:text-foreground">
              {BUSINESS.email}
            </a>
          </div>
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground">Mailing address</h2>
            <address className="mt-2 space-y-0.5 text-sm not-italic text-muted-foreground">
              {ADDRESS_LINES.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </address>
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-2xl px-4 pb-16 sm:px-6 lg:px-8">
        <form onSubmit={onSubmit} className="grid gap-4 rounded-3xl border border-border bg-card p-6">
          <input name="name" required placeholder="Your name" className="h-11 rounded-lg border border-input bg-background px-3 text-sm" />
          <input name="email" required type="email" placeholder="Email" className="h-11 rounded-lg border border-input bg-background px-3 text-sm" />
          <input name="subject" placeholder="Subject" className="h-11 rounded-lg border border-input bg-background px-3 text-sm" />
          <textarea name="message" required rows={5} placeholder="Message" className="rounded-lg border border-input bg-background p-3 text-sm" />
          <button disabled={loading} className="rounded-full bg-primary py-3 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90 disabled:opacity-60">
            {loading ? "Sending…" : "Send message"}
          </button>
        </form>
      </section>
    </EditablePageShell>
  );
}
